import { whoisLookup } from '../services/whois.js';
import { runTraceroute } from '../services/traceroute.js';
import { getLog, updateLog, updateThreat, updateEmailLog, getIpEnrichment, getAllLogs, getEmailLogsByIP } from '../services/storage.js';
import { updateGeoFromWhois, updateGeoFromShodan } from '../services/geoip.js';
import { html, SENSITIVE_PORTS, PORT_NAMES } from '../services/util.js';

const SETTINGS_KEY = 'threatscope_settings';

const template = document.createElement('template');
template.innerHTML = `
  <div class="detail-panel">
    <div class="detail-panel-header">
      <h2>Details</h2>
      <button class="btn btn-icon" id="close-detail">&times;</button>
    </div>
    <div class="detail-panel-body">
      <div id="detail-content">
        <div class="loading">
          <div class="spinner"></div>
          <span style="color:var(--text-muted)">Loading...</span>
        </div>
      </div>
    </div>
  </div>
`;

export class DetailPanel extends HTMLElement {
  constructor() {
    super();
    this.innerHTML = template.innerHTML;
    this._data = null;
  }

  connectedCallback() {
    this.querySelector('#close-detail').addEventListener('click', () => {
      const ev = new CustomEvent('threatscope:close-detail');
      document.dispatchEvent(ev);
      const overlay = this.closest('.detail-overlay');
      if (overlay) overlay.remove();
    });
  }

  set data(data) {
    this._data = data;
    this.render();
  }

  get data() {
    return this._data;
  }

  async render() {
    const content = this.querySelector('#detail-content');
    const d = this._data;

    if (!d) {
      content.innerHTML = '<div class="empty-state"><div class="empty-text">No data</div></div>';
      return;
    }

    if (d.type === 'log' && d.log) {
      await this.renderLogDetail(content, d.log, d.enrichment);
    } else if (d.type === 'threat' && d.threat) {
      await this.renderThreatDetail(content, d.threat, d.enrichment);
    } else if (d.type === 'emaillog' && d.emailLog) {
      await this.renderEmailLogDetail(content, d.emailLog, d.enrichment);
    } else if (d.type === 'ip') {
      if (d.ip) {
        await this.renderIPDetail(content, d.ip, d.enrichment);
      } else {
        content.innerHTML = `<div class="empty-state"><div class="empty-text">No IP address data provided</div></div>`;
      }
    } else {
      content.innerHTML = '<div class="empty-state"><div class="empty-text">Unknown detail type</div></div>';
    }

    // IP filter link: navigate to sign-in logs pane filtered by IP
    content.querySelectorAll('.ip-filter-link').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const ip = btn.dataset.ip;
        sessionStorage.setItem('threatscope_log_filter_ip', ip);
        document.dispatchEvent(new CustomEvent('threatscope:switch-pane', {
          detail: { pane: 'logs' }
        }));
      });
    });

    content.querySelectorAll('.detail-status').forEach(sel => {
      sel.addEventListener('change', async () => {
        const type = sel.dataset.type;
        const id = Number(sel.dataset.id);
        const newStatus = sel.value;
        if (type === 'log') {
          await updateLog(id, { status: newStatus });
        } else if (type === 'emaillog') {
          await updateEmailLog(id, { status: newStatus });
        } else {
          await updateThreat(id, { status: newStatus, classifiedAt: new Date().toISOString() });
        }
        document.dispatchEvent(new CustomEvent('threatscope:stats-update'));
      });
    });
  }

  async renderLogDetail(content, log, enrichment) {
    content.innerHTML = `
      <div class="detail-section">
        <div class="detail-section-title">Sign-In Event</div>
        <div class="detail-row">
          <span class="detail-label">Status</span>
          <span class="detail-value">
            <select class="status-select detail-status" data-type="log" data-id="${log.id}">
              <option value="investigating" ${log.status === 'investigating' ? 'selected' : ''}>Investigating</option>
              <option value="active" ${log.status === 'active' ? 'selected' : ''}>Active</option>
              <option value="resolved" ${log.status === 'resolved' ? 'selected' : ''}>Resolved</option>
            </select>
          </span>
        </div>
        <div class="detail-row"><span class="detail-label">Timestamp</span><span class="detail-value">${log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}</span></div>
        <div class="detail-row"><span class="detail-label">User</span><span class="detail-value">${html(log.user)}</span></div>
        <div class="detail-row"><span class="detail-label">IP Address</span><span class="detail-value"><code>${html(log.ip)}</code> <button class="btn btn-sm btn-link ip-filter-link" data-ip="${html(log.ip)}" title="View sign-in logs with this IP">View in Logs</button></span></div>
        <div class="detail-row"><span class="detail-label">Application</span><span class="detail-value">${html(log.app)}</span></div>
        <div class="detail-row"><span class="detail-label">Result</span><span class="detail-value">${html(log.result)}</span></div>
        <div class="detail-row"><span class="detail-label">Risk</span><span class="detail-value">${html(log.risk)}</span></div>
        <div class="detail-row"><span class="detail-label">Source</span><span class="detail-value">${html(log.source)}</span></div>
        <div class="detail-row"><span class="detail-label">Client App</span><span class="detail-value">${html(log.clientApp)}</span></div>
        <div class="detail-row"><span class="detail-label">Device</span><span class="detail-value">${html(log.device)}</span></div>
      </div>

      ${enrichment ? `
      <div class="detail-section">
        <div class="detail-section-title">GEOIP Location</div>
        <div class="detail-row"><span class="detail-label">Country</span><span class="detail-value">${html(enrichment.country)} (${html(enrichment.countryCode)})</span></div>
        <div class="detail-row"><span class="detail-label">City</span><span class="detail-value">${html(enrichment.city)}</span></div>
        <div class="detail-row"><span class="detail-label">Region</span><span class="detail-value">${html(enrichment.region)}</span></div>
        <div class="detail-row"><span class="detail-label">ISP</span><span class="detail-value">${html(enrichment.isp)}</span></div>
        <div class="detail-row"><span class="detail-label">Organization</span><span class="detail-value">${html(enrichment.org)}</span></div>
        <div class="detail-row"><span class="detail-label">ASN</span><span class="detail-value">${html(enrichment.asnName)} (${html(enrichment.asn)})</span></div>
        <div class="detail-row"><span class="detail-label">Coordinates</span><span class="detail-value">${enrichment.lat}, ${enrichment.lon}</span></div>
      </div>` : ''}

      ${enrichment?.threatIntel ? this.renderThreatIntel(enrichment.threatIntel) : ''}

      <div class="detail-section" id="whois-section">
        <div class="detail-section-title">WHOIS</div>
        <div id="whois-content"><span class="skeleton skeleton-text"></span></div>
        <button class="btn btn-sm btn-secondary" id="btn-whois" style="width:100%;margin-top:6px;">Refresh WHOIS</button>
      </div>

      <div class="detail-section" id="traceroute-section">
        <div class="detail-section-title">Traceroute</div>
        <button class="btn btn-sm btn-secondary" id="btn-traceroute" style="width:100%;margin-bottom:6px;">Run Traceroute</button>
        <div id="traceroute-result" style="margin-top: 8px;"></div>
      </div>
    `;

    if (enrichment && !enrichment.isPrivate && log.ip) {
      this.loadWhois(log.ip, content);
    } else {
      content.querySelector('#whois-content').innerHTML = '<span style="color:var(--text-muted)">Not available for private IPs</span>';
    }

    content.querySelector('#btn-whois').addEventListener('click', () => this.loadWhois(log.ip, content));
    content.querySelector('#btn-traceroute').addEventListener('click', () => this.runTraceroute(log.ip, content));

    if (enrichment?.threatIntel?.shodan?.countryName && (!enrichment.lat || enrichment.lookupFailed)) {
      this.checkShodanGeo(log.ip, enrichment.threatIntel.shodan);
    }
  }

  async renderThreatDetail(content, threat, enrichment) {
    let signInResult = '';
    if (threat.logId) {
      const log = await getLog(threat.logId);
      if (log) signInResult = log.result || 'Unknown';
    }

    content.innerHTML = `
      <div class="detail-section">
        <div class="detail-section-title">Threat Details</div>
        <div class="detail-row"><span class="detail-label">Type</span><span class="detail-value"><code>${html(threat.type.replace(/_/g, ' '))}</code></span></div>
        <div class="detail-row"><span class="detail-label">Severity</span><span class="detail-value"><span class="badge badge-severity-${threat.severity}">${threat.severity}</span></span></div>
        <div class="detail-row">
          <span class="detail-label">Status</span>
          <span class="detail-value">
            <select class="status-select detail-status" data-type="threat" data-id="${threat.id}">
              <option value="investigating" ${threat.status === 'investigating' ? 'selected' : ''}>Investigating</option>
              <option value="active" ${threat.status === 'active' ? 'selected' : ''}>Active</option>
              <option value="resolved" ${threat.status === 'resolved' ? 'selected' : ''}>Resolved</option>
            </select>
          </span>
        </div>
        <div class="detail-row"><span class="detail-label">IP</span><span class="detail-value"><code>${html(threat.ip)}</code> <button class="btn btn-sm btn-link ip-filter-link" data-ip="${html(threat.ip)}" title="View sign-in logs with this IP">View in Logs</button></span></div>
        <div class="detail-row"><span class="detail-label">User</span><span class="detail-value">${html(threat.user)}</span></div>
        <div class="detail-row"><span class="detail-label">Sign-In Result</span><span class="detail-value"><span class="badge ${signInResult === 'Success' ? 'badge-status-resolved' : signInResult && signInResult !== '-' ? 'badge-status-active' : ''}">${html(signInResult || 'Unknown')}</span></span></div>
        <div class="detail-row"><span class="detail-label">Timestamp</span><span class="detail-value">${threat.timestamp ? new Date(threat.timestamp).toLocaleString() : '-'}</span></div>
        <div class="detail-row"><span class="detail-label">Detected</span><span class="detail-value">${threat.detectedAt ? new Date(threat.detectedAt).toLocaleString() : '-'}</span></div>
      </div>

      ${threat.details ? this.renderThreatDetails(threat) : ''}

      ${enrichment ? `
      <div class="detail-section">
        <div class="detail-section-title">GEOIP</div>
        <div class="detail-row"><span class="detail-label">Country</span><span class="detail-value">${html(enrichment.country)} (${html(enrichment.countryCode)})</span></div>
        <div class="detail-row"><span class="detail-label">City</span><span class="detail-value">${html(enrichment.city)}</span></div>
        <div class="detail-row"><span class="detail-label">Region</span><span class="detail-value">${html(enrichment.region)}</span></div>
        <div class="detail-row"><span class="detail-label">ISP</span><span class="detail-value">${html(enrichment.isp)}</span></div>
        <div class="detail-row"><span class="detail-label">Organization</span><span class="detail-value">${html(enrichment.org)}</span></div>
        <div class="detail-row"><span class="detail-label">ASN</span><span class="detail-value">${html(enrichment.asnName)} (${html(enrichment.asn)})</span></div>
        <div class="detail-row"><span class="detail-label">Coordinates</span><span class="detail-value">${enrichment.lat}, ${enrichment.lon}</span></div>
      </div>` : ''}

      ${enrichment?.threatIntel ? this.renderThreatIntel(enrichment.threatIntel) : ''}

      <div class="detail-section" id="email-ref-section" style="display:none">
        <div class="detail-section-title">Email Log Reference</div>
        <div id="email-ref-content"></div>
      </div>

      <div class="detail-section" id="whois-section">
        <div class="detail-section-title">WHOIS</div>
        <div id="whois-content"><span class="skeleton skeleton-text"></span></div>
        <button class="btn btn-sm btn-secondary" id="btn-whois" style="width:100%;margin-top:6px;">Refresh WHOIS</button>
      </div>

      <div class="detail-section" id="traceroute-section">
        <div class="detail-section-title">Traceroute</div>
        <button class="btn btn-sm btn-secondary" id="btn-traceroute" style="width:100%;margin-bottom:6px;">Run Traceroute</button>
        <div id="traceroute-result" style="margin-top: 8px;"></div>
      </div>
    `;

    if (enrichment && !enrichment.isPrivate && threat.ip) {
      this.loadWhois(threat.ip, content);
    } else {
      content.querySelector('#whois-content').innerHTML = '<span style="color:var(--text-muted)">Not available for private IPs</span>';
    }

    content.querySelector('#btn-whois').addEventListener('click', () => this.loadWhois(threat.ip, content));
    content.querySelector('#btn-traceroute').addEventListener('click', () => this.runTraceroute(threat.ip, content));

    if (enrichment?.threatIntel?.shodan?.countryName && (!enrichment.lat || enrichment.lookupFailed)) {
      this.checkShodanGeo(threat.ip, enrichment.threatIntel.shodan);
    }

    // Load email cross-reference data
    this.loadEmailRef(threat.ip, content);
  }

  async loadEmailRef(ip, content) {
    const section = content.querySelector('#email-ref-section');
    const refContent = content.querySelector('#email-ref-content');
    if (!section || !refContent) return;
    const emailLogs = await getEmailLogsByIP(ip);
    if (emailLogs && emailLogs.length > 0) {
      section.style.display = '';
      refContent.innerHTML = `
        <div style="padding:8px 0;line-height:1.5;color:var(--text-secondary);font-size:0.85rem">
          This IP was also found in email logs:
        </div>
        <div class="detail-row"><span class="detail-label">Email Records</span><span class="detail-value"><span class="badge badge-email-ref">${emailLogs.length} records</span></span></div>
      `;
    }
  }

  async renderEmailLogDetail(content, emailLog, enrichment) {
    const ip = emailLog.senderIp;

    content.innerHTML = `
      <div class="detail-section">
        <div class="detail-section-title">Email Log</div>
        <div class="detail-row">
          <span class="detail-label">Status</span>
          <span class="detail-value">
            <select class="status-select detail-status" data-type="emaillog" data-id="${emailLog.id}">
              <option value="investigating" ${emailLog.status === 'investigating' ? 'selected' : ''}>Investigating</option>
              <option value="active" ${emailLog.status === 'active' ? 'selected' : ''}>Active</option>
              <option value="resolved" ${emailLog.status === 'resolved' ? 'selected' : ''}>Resolved</option>
            </select>
          </span>
        </div>
        <div class="detail-row"><span class="detail-label">Timestamp</span><span class="detail-value">${emailLog.timestamp ? new Date(emailLog.timestamp).toLocaleString() : '-'}</span></div>
        <div class="detail-row"><span class="detail-label">Sender</span><span class="detail-value">${html(emailLog.sender)}</span></div>
        <div class="detail-row"><span class="detail-label">Recipients</span><span class="detail-value">${html(emailLog.recipients)}</span></div>
        <div class="detail-row"><span class="detail-label">Subject</span><span class="detail-value">${html(emailLog.subject)}</span></div>
        <div class="detail-row"><span class="detail-label">Message-ID</span><span class="detail-value"><code>${html(emailLog.messageId)}</code></span></div>
        <div class="detail-row"><span class="detail-label">Sender IP</span><span class="detail-value"><code>${html(ip)}</code> <button class="btn btn-sm btn-link ip-filter-link" data-ip="${html(ip)}" title="View sign-in logs with this IP">View in Logs</button></span></div>
        <div class="detail-row"><span class="detail-label">Source</span><span class="detail-value"><span class="badge badge-source-${html(emailLog.source)}">${html(emailLog.source)}</span></span></div>
      </div>

      ${enrichment ? `
      <div class="detail-section">
        <div class="detail-section-title">GEOIP Location</div>
        <div class="detail-row"><span class="detail-label">Country</span><span class="detail-value">${html(enrichment.country)} (${html(enrichment.countryCode)})</span></div>
        <div class="detail-row"><span class="detail-label">City</span><span class="detail-value">${html(enrichment.city)}</span></div>
        <div class="detail-row"><span class="detail-label">Region</span><span class="detail-value">${html(enrichment.region)}</span></div>
        <div class="detail-row"><span class="detail-label">ISP</span><span class="detail-value">${html(enrichment.isp)}</span></div>
        <div class="detail-row"><span class="detail-label">Organization</span><span class="detail-value">${html(enrichment.org)}</span></div>
        <div class="detail-row"><span class="detail-label">ASN</span><span class="detail-value">${html(enrichment.asnName)} (${html(enrichment.asn)})</span></div>
        <div class="detail-row"><span class="detail-label">Coordinates</span><span class="detail-value">${enrichment.lat}, ${enrichment.lon}</span></div>
      </div>` : ''}

      ${enrichment?.threatIntel ? this.renderThreatIntel(enrichment.threatIntel) : ''}

      <div class="detail-section" id="whois-section">
        <div class="detail-section-title">WHOIS</div>
        <div id="whois-content"><span class="skeleton skeleton-text"></span></div>
        <button class="btn btn-sm btn-secondary" id="btn-whois" style="width:100%;margin-top:6px;">Refresh WHOIS</button>
      </div>

      <div class="detail-section" id="traceroute-section">
        <div class="detail-section-title">Traceroute</div>
        <button class="btn btn-sm btn-secondary" id="btn-traceroute" style="width:100%;margin-bottom:6px;">Run Traceroute</button>
        <div id="traceroute-result" style="margin-top: 8px;"></div>
      </div>
    `;

    if (enrichment && !enrichment.isPrivate && ip) {
      this.loadWhois(ip, content);
    } else {
      content.querySelector('#whois-content').innerHTML = '<span style="color:var(--text-muted)">Not available for private IPs</span>';
    }

    content.querySelector('#btn-whois').addEventListener('click', () => this.loadWhois(ip, content));
    content.querySelector('#btn-traceroute').addEventListener('click', () => this.runTraceroute(ip, content));

    if (enrichment?.threatIntel?.shodan?.countryName && (!enrichment.lat || enrichment.lookupFailed)) {
      this.checkShodanGeo(ip, enrichment.threatIntel.shodan);
    }
  }

  async renderIPDetail(content, ip, enrichment) {
    content.innerHTML = `
      <div class="detail-section">
        <div class="detail-section-title">IP Address</div>
        <div class="detail-row"><span class="detail-label">IP</span><span class="detail-value"><code>${html(ip)}</code> <button class="btn btn-sm btn-link ip-filter-link" data-ip="${html(ip)}" title="View sign-in logs with this IP">View in Logs</button></span></div>
      </div>

      ${enrichment ? `
      <div class="detail-section">
        <div class="detail-section-title">GEOIP Location</div>
        <div class="detail-row"><span class="detail-label">Country</span><span class="detail-value">${html(enrichment.country)} (${html(enrichment.countryCode)})</span></div>
        <div class="detail-row"><span class="detail-label">City</span><span class="detail-value">${html(enrichment.city)}</span></div>
        <div class="detail-row"><span class="detail-label">Region</span><span class="detail-value">${html(enrichment.region)}</span></div>
        <div class="detail-row"><span class="detail-label">ISP</span><span class="detail-value">${html(enrichment.isp)}</span></div>
        <div class="detail-row"><span class="detail-label">Organization</span><span class="detail-value">${html(enrichment.org)}</span></div>
        <div class="detail-row"><span class="detail-label">ASN</span><span class="detail-value">${html(enrichment.asnName)} (${html(enrichment.asn)})</span></div>
        <div class="detail-row"><span class="detail-label">Coordinates</span><span class="detail-value">${enrichment.lat}, ${enrichment.lon}</span></div>
        ${enrichment.isPrivate ? '<div class="detail-row"><span class="detail-label">Note</span><span class="detail-value" style="color:var(--accent-yellow)">Private IP — limited enrichment available</span></div>' : ''}
      </div>` : ''}

      ${enrichment?.threatIntel ? this.renderThreatIntel(enrichment.threatIntel) : ''}

      <div class="detail-section" id="whois-section">
        <div class="detail-section-title">WHOIS</div>
        <div id="whois-content"><span class="skeleton skeleton-text"></span></div>
        <button class="btn btn-sm btn-secondary" id="btn-whois" style="width:100%;margin-top:6px;">Refresh WHOIS</button>
      </div>

      <div class="detail-section" id="traceroute-section">
        <div class="detail-section-title">Traceroute</div>
        <button class="btn btn-sm btn-secondary" id="btn-traceroute" style="width:100%;margin-bottom:6px;">Run Traceroute</button>
        <div id="traceroute-result" style="margin-top: 8px;"></div>
      </div>
    `;

    if (enrichment && !enrichment.isPrivate && ip) {
      this.loadWhois(ip, content);
    } else {
      content.querySelector('#whois-content').innerHTML = '<span style="color:var(--text-muted)">Not available for private IPs</span>';
    }

    content.querySelector('#btn-whois').addEventListener('click', () => this.loadWhois(ip, content));
    content.querySelector('#btn-traceroute').addEventListener('click', () => this.runTraceroute(ip, content));

    if (enrichment?.threatIntel?.shodan?.countryName && (!enrichment.lat || enrichment.lookupFailed)) {
      this.checkShodanGeo(ip, enrichment.threatIntel.shodan);
    }
  }

  async checkShodanGeo(ip, shodanData) {
    const updated = await updateGeoFromShodan(ip, shodanData);
    if (updated) {
      document.dispatchEvent(new CustomEvent('threatscope:stats-update'));
    }
  }

  async loadWhois(ip, content) {
    const whoisContent = content.querySelector('#whois-content');
    whoisContent.innerHTML = '<div class="skeleton skeleton-text" style="width:80%"></div><div class="skeleton skeleton-text" style="width:60%"></div><div class="skeleton skeleton-text" style="width:50%"></div>';

    const whoisData = await whoisLookup(ip);

    const enrichment = await getIpEnrichment(ip);
    const needsGeoUpdate = enrichment && (!enrichment.lat || enrichment.lookupFailed);

    if (whoisData) {
      if (needsGeoUpdate) {
        const updated = await updateGeoFromWhois(ip, whoisData);
        if (updated) {
          document.dispatchEvent(new CustomEvent('threatscope:stats-update'));
        }
      }

      const geoNote = needsGeoUpdate && whoisData.country
        ? `<div style="color:var(--accent-blue);font-size:0.72rem;padding:4px 0 8px;border-bottom:1px solid var(--border-subtle);margin-bottom:8px">GEOIP populated from WHOIS (${html(whoisData.country)})</div>`
        : '';

      whoisContent.innerHTML = `
        ${geoNote}
        <div class="detail-row"><span class="detail-label">Handle</span><span class="detail-value">${html(whoisData.handle)}</span></div>
        <div class="detail-row"><span class="detail-label">Organization</span><span class="detail-value">${html(whoisData.name)}</span></div>
        <div class="detail-row"><span class="detail-label">Country</span><span class="detail-value">${html(whoisData.country) || '-'}</span></div>
        <div class="detail-row"><span class="detail-label">CIDR</span><span class="detail-value">${html(whoisData.cidr)}</span></div>
        <div class="detail-row"><span class="detail-label">Range</span><span class="detail-value">${html(whoisData.startAddress)} - ${html(whoisData.endAddress)}</span></div>
        <div class="detail-row"><span class="detail-label">Registered</span><span class="detail-value">${whoisData.registrationDate ? new Date(whoisData.registrationDate).toLocaleDateString() : '-'}</span></div>
        <div class="detail-row"><span class="detail-label">Abuse Email</span><span class="detail-value">${html(whoisData.abuseEmail) || '-'}</span></div>
      `;
    } else {
      whoisContent.innerHTML = '<span style="color:var(--text-muted)">WHOIS data not found — click Refresh to try again</span>';
    }
  }

  async runTraceroute(ip, content) {
    const settings = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}');
    const workerUrl = settings.workerUrl || '';
    const resultDiv = content.querySelector('#traceroute-result');
    resultDiv.innerHTML = '<div style="padding:8px 0"><div class="skeleton skeleton-text"></div><div class="skeleton skeleton-text"></div><div class="skeleton skeleton-text"></div><div class="skeleton skeleton-text" style="width:40%"></div></div>';

    const result = await runTraceroute(ip, workerUrl);
    if (result.error) {
      resultDiv.innerHTML = `<div class="traceroute-output" style="color: var(--accent-red);">Error: ${html(result.error)}</div>`;
    } else if (result.hops) {
      resultDiv.innerHTML = `<div class="traceroute-output">${result.hops.map(h => `<span class="${h.rtt === '*' ? 'hop-timeout' : h.rtt > 100 ? 'hop-slow' : 'hop-good'}">${h.hop}. ${h.ip || '*'} (${h.rtt}ms)</span>`).join('\n')}</div>`;
    } else {
      resultDiv.innerHTML = `<div class="traceroute-output">${html(JSON.stringify(result, null, 2))}</div>`;
    }
  }

  renderThreatDetails(threat) {
    const d = threat.details;
    const geo = d.ipCountry ? `${d.ipCity ? d.ipCity + ', ' : ''}${d.ipCountry}${d.ipIsp ? ' (' + d.ipIsp + ')' : ''}` : '';
    let explanation = '';
    let extraRows = '';

    switch (threat.type) {
      case 'impossible_travel':
        explanation = `User ${html(threat.user)} logged in from ${html(d.toLocation)} after a previous login from ${html(d.fromLocation)} — ${d.distanceKm} km away in just ${d.timeDiffMinutes} minutes. This is faster than physically possible, indicating a stolen session or credential sharing.`;
        if (geo) explanation += ` Location context: ${geo}.`;
        extraRows = `
          <div class="detail-row"><span class="detail-label">From</span><span class="detail-value">${html(d.fromLocation)} (IP: ${html(d.fromIP)})</span></div>
          <div class="detail-row"><span class="detail-label">To</span><span class="detail-value">${html(d.toLocation)}</span></div>
          <div class="detail-row"><span class="detail-label">Distance</span><span class="detail-value">${d.distanceKm} km</span></div>
          <div class="detail-row"><span class="detail-label">Time Diff</span><span class="detail-value">${d.timeDiffMinutes} min</span></div>
        `;
        break;

      case 'known_bad_ip':
        if (d.source === 'AbuseIPDB') {
          explanation = `IP ${html(threat.ip)} has been reported to AbuseIPDB with an abuse confidence score of ${d.score}% (${d.reports} reports).`;
          extraRows = `
            <div class="detail-row"><span class="detail-label">Source</span><span class="detail-value">AbuseIPDB — Score ${d.score}%</span></div>
            <div class="detail-row"><span class="detail-label">Reports</span><span class="detail-value">${d.reports}</span></div>
          `;
        } else if (d.source === 'AlienVault OTX') {
          explanation = `IP ${html(threat.ip)} appears in ${d.pulses} AlienVault OTX threat pulse(s), indicating known malicious activity.`;
          extraRows = `
            <div class="detail-row"><span class="detail-label">Source</span><span class="detail-value">AlienVault OTX — ${d.pulses} pulses</span></div>
          `;
        } else if (d.source === 'GreyNoise') {
          explanation = `GreyNoise classifies IP ${html(threat.ip)} as "${html(d.classification)}"${d.noise ? ' and flags it as internet noise (automated scanning/probing)' : ''}, indicating a known threat.`;
          extraRows = `
            <div class="detail-row"><span class="detail-label">Source</span><span class="detail-value">GreyNoise — ${html(d.classification)}</span></div>
          `;
        }
        if (geo) extraRows += `<div class="detail-row"><span class="detail-label">Location</span><span class="detail-value">${geo}</span></div>`;
        break;

      case 'failure_spike':
        explanation = `IP ${html(threat.ip)} generated ${d.failureCount} failed sign-in attempts within a ${d.timeWindowMinutes}-minute window, suggesting a password guessing attack.`;
        extraRows = `
          <div class="detail-row"><span class="detail-label">Failures</span><span class="detail-value">${d.failureCount}</span></div>
          <div class="detail-row"><span class="detail-label">Time Window</span><span class="detail-value">${d.timeWindowMinutes} min</span></div>
        `;
        if (geo) extraRows += `<div class="detail-row"><span class="detail-label">Location</span><span class="detail-value">${geo}</span></div>`;
        break;

      case 'brute_force':
        explanation = `IP ${html(threat.ip)} generated ${d.failureCount} failed sign-in attempts — significantly more than a typical failure spike, indicating a sustained brute-force attack.`;
        extraRows = `
          <div class="detail-row"><span class="detail-label">Failures</span><span class="detail-value">${d.failureCount}</span></div>
        `;
        if (geo) extraRows += `<div class="detail-row"><span class="detail-label">Location</span><span class="detail-value">${geo}</span></div>`;
        break;

      case 'new_country':
        explanation = `User ${html(threat.user)} signed in from a new country: ${html(d.country)}. Previous sign-ins were from ${html(d.knownCountries.join(', '))}. This could indicate a compromised account or legitimate travel.`;
        extraRows = `
          <div class="detail-row"><span class="detail-label">New Country</span><span class="detail-value">${html(d.country)}</span></div>
          <div class="detail-row"><span class="detail-label">Known Countries</span><span class="detail-value">${html(d.knownCountries.join(', '))}</span></div>
        `;
        if (geo) extraRows += `<div class="detail-row"><span class="detail-label">Location</span><span class="detail-value">${geo}</span></div>`;
        break;

      case 'anomalous_time':
        explanation = `User ${html(threat.user)} signed in at an unusual hour (UTC ${d.hour}:00) from ${html(threat.ip)}. Sign-ins outside normal business hours (UTC 0–5) may indicate a compromised account or automated access.`;
        extraRows = `
          <div class="detail-row"><span class="detail-label">Hour (UTC)</span><span class="detail-value">${d.hour}:00</span></div>
          <div class="detail-row"><span class="detail-label">Time (UTC)</span><span class="detail-value">${html(d.utcTime)}</span></div>
        `;
        if (geo) extraRows += `<div class="detail-row"><span class="detail-label">Location</span><span class="detail-value">${geo}</span></div>`;
        break;

      case 'legacy_auth':
        explanation = `User ${html(threat.user)} authenticated using a legacy protocol (${html(d.clientApp)}). Legacy protocols like IMAP, POP3, and SMTP do not support modern authentication controls like MFA.`;
        extraRows = `
          <div class="detail-row"><span class="detail-label">Client App</span><span class="detail-value">${html(d.clientApp)}</span></div>
        `;
        if (geo) extraRows += `<div class="detail-row"><span class="detail-label">Location</span><span class="detail-value">${geo}</span></div>`;
        break;

      default:
        explanation = `Threat type "${threat.type}" was detected for IP ${html(threat.ip)}.`;
        extraRows = Object.entries(d)
          .filter(([k]) => !k.startsWith('ip'))
          .map(([k, v]) => `<div class="detail-row"><span class="detail-label">${k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</span><span class="detail-value">${html(String(v))}</span></div>`)
          .join('');
        if (geo) extraRows += `<div class="detail-row"><span class="detail-label">Location</span><span class="detail-value">${geo}</span></div>`;
    }

    return `
      <div class="detail-section">
        <div class="detail-section-title">Why This Was Flagged</div>
        <div style="padding: 8px 0; line-height: 1.5; color: var(--text-secondary); font-size: 0.85rem;">${explanation}</div>
      </div>
      <div class="detail-section">
        <div class="detail-section-title">Threat Data</div>
        ${extraRows}
      </div>`;
  }

  renderThreatIntel(threatIntel) {
    let htmlOut = '<div class="detail-section"><div class="detail-section-title">Threat Intel</div>';
    if (threatIntel.abuseIPDB) {
      const a = threatIntel.abuseIPDB;
      htmlOut += `
        <div class="detail-row"><span class="detail-label">AbuseIPDB Score</span><span class="detail-value" style="color: ${a.abuseConfidenceScore >= 50 ? 'var(--accent-red)' : a.abuseConfidenceScore >= 25 ? 'var(--accent-orange)' : 'var(--accent-green)'}">${a.abuseConfidenceScore}%</span></div>
        <div class="detail-row"><span class="detail-label">Total Reports</span><span class="detail-value">${a.totalReports}</span></div>
        <div class="detail-row"><span class="detail-label">Usage Type</span><span class="detail-value">${html(a.usageType || '-')}</span></div>
        <div class="detail-row"><span class="detail-label">Whitelisted</span><span class="detail-value">${a.isWhitelisted ? 'Yes' : 'No'}</span></div>
      `;
    }
    if (threatIntel.alienVault) {
      const av = threatIntel.alienVault;
      htmlOut += `
        <div class="detail-row"><span class="detail-label">AlienVault Pulses</span><span class="detail-value">${av.pulseCount}</span></div>
        ${(av.pulses || []).slice(0, 3).map(p => `<div class="detail-row"><span class="detail-label">Pulse</span><span class="detail-value" title="${html(p.description || '')}">${html(p.name)}</span></div>`).join('')}
      `;
    }
    if (threatIntel.greynoise) {
      const g = threatIntel.greynoise;
      const communityData = g.community;
      const enterpriseData = g.enterprise;
      if (communityData) {
        htmlOut += `
          <div class="detail-row"><span class="detail-label">GreyNoise Class</span><span class="detail-value" style="color: ${communityData.classification === 'malicious' ? 'var(--accent-red)' : communityData.classification === 'unknown' ? 'var(--text-muted)' : 'var(--accent-green)'}">${html(communityData.classification)}</span></div>
          <div class="detail-row"><span class="detail-label">Noise</span><span class="detail-value">${communityData.noise ? 'Yes' : 'No'}</span></div>
          <div class="detail-row"><span class="detail-label">RIOT</span><span class="detail-value">${communityData.riot ? 'Yes (benign)' : 'No'}</span></div>
        `;
      }
      if (enterpriseData) {
        htmlOut += `
          <div class="detail-row"><span class="detail-label">GN Noise Score</span><span class="detail-value">${enterpriseData.noiseScore}</span></div>
          <div class="detail-row"><span class="detail-label">GN Actor</span><span class="detail-value">${html(enterpriseData.actor || '-')}</span></div>
          <div class="detail-row"><span class="detail-label">Tags</span><span class="detail-value">${(enterpriseData.tags || []).join(', ') || '-'}</span></div>
          <div class="detail-row"><span class="detail-label">Bot</span><span class="detail-value">${enterpriseData.bot ? 'Yes' : 'No'}</span></div>
          <div class="detail-row"><span class="detail-label">VPN</span><span class="detail-value">${enterpriseData.vpn ? 'Yes' : 'No'}</span></div>
          <div class="detail-row"><span class="detail-label">TOR</span><span class="detail-value">${enterpriseData.tor ? 'Yes' : 'No'}</span></div>
          <div class="detail-row"><span class="detail-label">Proxy</span><span class="detail-value">${enterpriseData.proxy ? 'Yes' : 'No'}</span></div>
          <div class="detail-row"><span class="detail-label">Last Seen</span><span class="detail-value">${html(enterpriseData.lastSeen || '-')}</span></div>
        `;
      }
    }
    if (threatIntel.shodan) {
      const s = threatIntel.shodan;
      htmlOut += `
        <div style="margin-top:10px;padding-top:10px;border-top:1px solid var(--border-subtle)">
          <div class="detail-section-title" style="margin-bottom:6px">Shodan</div>
          <div class="detail-row"><span class="detail-label">Open Ports</span><span class="detail-value">${this.renderShodanPorts(s.ports)}</span></div>
          ${s.totalPorts > 0 ? `<div class="detail-row"><span class="detail-label">Total Ports</span><span class="detail-value">${s.totalPorts}</span></div>` : ''}
          ${s.hostnames.length > 0 ? `<div class="detail-row"><span class="detail-label">Hostnames</span><span class="detail-value">${html(s.hostnames.join(', '))}</span></div>` : ''}
          ${s.org ? `<div class="detail-row"><span class="detail-label">Organization</span><span class="detail-value">${html(s.org)}</span></div>` : ''}
          ${s.isp ? `<div class="detail-row"><span class="detail-label">ISP</span><span class="detail-value">${html(s.isp)}</span></div>` : ''}
          ${s.os ? `<div class="detail-row"><span class="detail-label">OS</span><span class="detail-value">${html(s.os)}</span></div>` : ''}
          ${s.tags.length > 0 ? `<div class="detail-row"><span class="detail-label">Tags</span><span class="detail-value" style="display:flex;flex-wrap:wrap;gap:4px;justify-content:flex-end">${s.tags.map(t => `<span class="badge badge-source-entraid" style="font-size:0.65rem">${html(t)}</span>`).join(' ')}</span></div>` : ''}
          ${s.totalVulns > 0 ? `<div class="detail-row"><span class="detail-label">CVEs</span><span class="detail-value"><span class="badge badge-severity-${s.totalVulns > 5 ? 'critical' : s.totalVulns > 2 ? 'high' : 'medium'}">${s.totalVulns} CVE${s.totalVulns !== 1 ? 's' : ''}</span></span></div>` : ''}
          ${s.vulns.length > 0 ? `<div class="detail-row"><span class="detail-label">Top CVEs</span><span class="detail-value" style="max-width:70%">${s.vulns.slice(0, 5).map(v => `<code style="font-size:0.7rem;display:inline-block;margin:1px 0">${html(v.cve)}${v.published ? ' (' + v.published + ')' : ''}</code>`).join('<br>')}</span></div>` : ''}
          ${s.lastUpdate ? `<div class="detail-row"><span class="detail-label">Last Scanned</span><span class="detail-value">${new Date(s.lastUpdate).toLocaleString()}</span></div>` : ''}
        </div>`;
    }
    htmlOut += '</div>';
    return htmlOut;
  }

  renderShodanPorts(ports) {
    if (!ports || ports.length === 0) return '<span style="color:var(--text-muted);font-style:italic">None detected</span>';
    const displayed = ports.slice(0, 12);
    const overflow = ports.length > 12 ? ` ${ports.length - 12}+` : '';
    return displayed.map(p => {
      const name = PORT_NAMES[p] || '';
      const isSensitive = SENSITIVE_PORTS.includes(p);
      const label = name ? `${p} (${name})` : `${p}`;
      return `<code style="${isSensitive ? 'color:var(--accent-red);background:var(--accent-red-dim)' : ''};font-size:0.72rem;display:inline-block;margin:1px ${overflow ? '1px' : '2px'}">${label}</code>`;
    }).join(' ') + overflow;
  }

}

customElements.define('detail-panel', DetailPanel);
